import { supabase } from '../integrations/supabase/client';

export type WebhookEventType = 
  | 'client.created'
  | 'client.updated'
  | 'client.suspended'
  | 'onboarding.started'
  | 'onboarding.completed'
  | 'onboarding.cancelled'
  | 'user.invited'
  | 'user.activated'
  | 'user.deactivated'
  | 'credit.applied'
  | 'credit.approved'
  | 'credit.declined'
  | 'hierarchy.created'
  | 'hierarchy.updated'
  | 'config.updated';

interface TriggerWebhookParams {
  event_type: WebhookEventType;
  resource_type: string;
  resource_id: string;
  data: any;
  tenant_id?: string;
}

/**
 * Triggers webhook delivery for platform events
 * This function should be called after any significant platform action
 */
export async function triggerWebhook(params: TriggerWebhookParams): Promise<void> {
  try {
    // Triggering webhook event
    
    const { data, error } = await supabase.functions.invoke('trigger-webhook-event', {
      body: params
    });

    if (error) {
      console.error('[Webhook] Trigger error:', error);
      // Don't throw - webhook failures shouldn't break main operations
      return;
    }

    // Webhook triggered successfully
  } catch (error) {
    console.error('[Webhook] Trigger exception:', error);
    // Silently fail - webhook is not critical path
  }
}

/**
 * Convenience functions for common webhook triggers
 */
export const webhooks = {
  clientCreated: (clientId: string, data: any) =>
    triggerWebhook({ event_type: 'client.created', resource_type: 'client', resource_id: clientId, data, tenant_id: clientId }),

  clientUpdated: (clientId: string, data: any) =>
    triggerWebhook({ event_type: 'client.updated', resource_type: 'client', resource_id: clientId, data, tenant_id: clientId }),

  onboardingCompleted: (flowId: string, clientId: string, data: any) =>
    triggerWebhook({ event_type: 'onboarding.completed', resource_type: 'onboarding_flow', resource_id: flowId, data, tenant_id: clientId }),

  creditApproved: (applicationId: string, clientId: string, data: any) =>
    triggerWebhook({ event_type: 'credit.approved', resource_type: 'credit_application', resource_id: applicationId, data, tenant_id: clientId }),

  userInvited: (userId: string, clientId: string, data: any) =>
    triggerWebhook({ event_type: 'user.invited', resource_type: 'user', resource_id: userId, data, tenant_id: clientId }),

  hierarchyCreated: (hierarchyId: string, parentClientId: string, data: any) =>
    triggerWebhook({ event_type: 'hierarchy.created', resource_type: 'client_hierarchy', resource_id: hierarchyId, data, tenant_id: parentClientId }),

  configUpdated: (configId: string, clientId: string, data: any) =>
    triggerWebhook({ event_type: 'config.updated', resource_type: 'client_configuration', resource_id: configId, data, tenant_id: clientId }),
};
